import React from 'react';
import {
  isRouteErrorResponse,
  useRouteError,
  useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { selectLogonUrls } from 'store/appSlice';
import { clearSession, redirectToLogin } from 'lib/router';
import { getSearchParmFromLocation } from 'swipeclock-frontend';
import { setPublicPageTitle } from 'helpers/documentHelper';
import { styled } from '@mui/material/styles';
import { ReactComponent as WFHLogo } from 'assets/WorkforceHubDarkLogo.svg';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';

export default function ErrorBoundary() {
  const error = useRouteError();
  const location = useLocation();
  const searchParamStatus = getSearchParmFromLocation(location, 'status');
  const status = isRouteErrorResponse(error) ? error.status : 400;
  const errorStatus = Number(searchParamStatus) || status;

  setPublicPageTitle('Error');

  console.error('ErrorBoundary caught an error');
  console.error(error);

  if (errorStatus) {
    switch (errorStatus) {
      case 401:
        return <Unauthorized />;
      case 403:
        return <Forbidden />;
      case 404:
        return <NotFound />;
      case 500:
        return <ServerError />;
      case 503:
        return <ServiceUnavailable />;
      default:
        return <UnknownError />;
    }
  }

  if (error) {
    console.error('ErrorBounday caught an unknown error');
    console.error(error);
  } else {
    console.warn(
      'Error is not defined in the ErrorBoundary. This can happen if the error was a response and caught in a defer loader.'
    );
  }

  return <UnknownError />;
}

function Unauthorized() {
  const dispatch = useDispatch();
  const logonUrls = useSelector(selectLogonUrls);
  console.warn('Unauthorized. Redirecting to login.');
  // I don't believe these are necessary, but just in case
  // it gets past loader checks, will invalidate token here too.
  clearSession(dispatch);
  redirectToLogin(logonUrls);
  return null;
}

function Forbidden() {
  return (
    <ErrorPageWrapper>
      <ErrorBox>
        <Typography variant="h4">403 Forbidden</Typography>
        <Typography variant="body2">
          You do not have permission to access this page.
        </Typography>
      </ErrorBox>
    </ErrorPageWrapper>
  );
}

function NotFound() {
  return (
    <ErrorPageWrapper>
      <ErrorBox>
        <Typography variant="h4">404 Not Found</Typography>
        <Typography variant="body2">
          The page you're looking for doesn't exist.
        </Typography>
      </ErrorBox>
    </ErrorPageWrapper>
  );
}

function ServerError() {
  return (
    <ErrorPageWrapper>
      <ErrorBox>
        <Typography variant="h4">500 Server Error</Typography>
        <Typography variant="body2">
          Something went wrong on our end.
        </Typography>
      </ErrorBox>
    </ErrorPageWrapper>
  );
}

function ServiceUnavailable() {
  return (
    <ErrorPageWrapper>
      <ErrorBox>
        <Typography variant="h4">503 Service Unavailable</Typography>
        <Typography variant="body2">
          Our service is currently unavailable. Please try again later.
        </Typography>
      </ErrorBox>
    </ErrorPageWrapper>
  );
}

function UnknownError() {
  return (
    <ErrorPageWrapper>
      <ErrorBox>
        <Typography variant="h4">Unknown Error</Typography>
        <Typography variant="body2">An unknown error occurred.</Typography>
      </ErrorBox>
    </ErrorPageWrapper>
  );
}

function ErrorPageWrapper({ children }) {
  return (
    <Background>
      <Grid>
        <CenterColumn>
          <Toolbar />
          {children}
          <LogoContainer>
            <WFHLogo />
          </LogoContainer>
        </CenterColumn>
      </Grid>
    </Background>
  );
}

ErrorPageWrapper.propTypes = {
  children: PropTypes.any,
};

// ########################################################
// ### STYLED COMPONENTS
// ########################################################

const Background = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.navyBlue.main,
  height: '100svh',
}));

const Grid = styled((props) => <Box component="main" {...props} />)(
  ({ theme }) => ({
    display: 'grid',
    gridTemplateAreas: '"left center right"', // Name the grid areas
    gridTemplateColumns: '16px 1fr 16px', // Left, center, and right columns
    alignItems: 'flex-start',
    justifyContent: 'center',

    [theme.breakpoints.up('sm')]: {
      gridTemplateAreas: '"left center right"',
      gridTemplateColumns: '0.25fr 1fr 0.25fr',
    },

    [theme.breakpoints.up('md')]: {
      gridTemplateAreas: '"left center right"',
      gridTemplateColumns: '1fr 1fr 1fr',
    },

    [theme.breakpoints.up('lg')]: {
      gridTemplateAreas: '"left center right"',
      gridTemplateColumns: '1fr auto 1fr',
    },
  })
);

const CenterColumn = styled(Box)(({ theme }) => ({
  gridArea: 'center', // Place this component in the center area
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(3),
  margin: '0 auto',
  marginTop: theme.spacing(4),
  gap: theme.spacing(4),
  width: '100%',
  [theme.breakpoints.up('md')]: {
    width: '720px',
  },
}));

const ErrorBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(4),
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.error.dark,
  width: '100%',
  borderRadius: theme.shape.borderRadius,
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  width: '80%',
  marginTop: theme.spacing(4),
}));
